import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { OidcAuthService } from './oidc-auth.service';
import { OidcProvider } from './oidc-providers';
import { b2cPolicies, rarePolicies } from '../onedrive/auth-config';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule],
  template: `
  <div class="login-wrap" [class.mobile]="mobile">
    <div class="palm-bg" aria-hidden="true">
      <svg viewBox="0 0 400 520" preserveAspectRatio="xMidYMax slice">
        <path d="M198 520 C196 390 190 300 206 232 C209 216 215 206 221 201 L233 206 C225 216 221 242 219 302 C217 384 215 452 215 520 Z" fill="#0a3b2e"/>
        <g fill="#0e5a44">
          <path d="M223 201 C181 150 121 129 60 139 C131 145 176 176 215 206 Z"/>
          <path d="M223 201 C251 139 321 118 381 129 C311 140 261 171 227 206 Z"/>
          <path d="M223 199 C201 129 191 58 211 8 C215 70 225 141 231 199 Z"/>
          <path d="M221 206 C161 201 101 221 56 261 C121 236 181 226 217 211 Z"/>
          <path d="M227 206 C291 201 351 226 391 266 C321 239 261 229 231 213 Z"/>
          <path d="M223 203 C191 176 141 176 96 196 C151 181 196 191 221 209 Z"/>
          <path d="M225 203 C261 176 306 179 346 199 C296 183 251 191 227 209 Z"/>
        </g>
      </svg>
    </div>
    <div class="login-card">
      <div class="brandline"></div>

      <!-- The banner now only speaks when it has something specific to say: which product
           this is (gene.clinic) or which tier the visitor chose (free). The default
           sign-in page carried "Beta release! / Early access now open", which told a
           returning user nothing and set a tentative tone on the first screen of the
           product. -->
      <div class="beta-banner" role="status" *ngIf="clinicMode || freeMode">
        <span class="beta-tag">{{ clinicMode ? 'gene.clinic' : 'Free version' }}</span>
        <span class="beta-text">{{ clinicMode ? clinicBanner : 'Sign in to continue' }}</span>
      </div>

      <div class="head" *ngIf="!clinicMode">
        <div class="logo"><img src="assets/logos/cypress.svg" alt="Sign in" /></div>
      </div>

      <!-- gene.clinic's own header. The yak is the BajaBio GeneTx Designer's mark, and a
           family arriving here has no reason to know it -- a logo you do not recognise on a
           sign-in page is a reason to hesitate, not a reassurance.

           Drawn inline rather than loaded as an asset: it ships with the bundle, so there is
           no second request, nothing to 404 on a domain whose assets are new, and no image
           to go missing from /eln later. -->
      <div class="head clinic-head" *ngIf="clinicMode">
        <div class="cl-mark" aria-hidden="true">
          <!-- The viewBox frames the tree rather than the square it was drawn in, so it
               fills the mark instead of sitting in the middle of a box. -->
          <svg viewBox="13 14 46 43" width="56" height="52">

        <g fill="none">
          <!-- THE LONE CYPRESS, drawn from the photograph.
               Three things do the work here. The crown is in TWO greens, a darker mass behind
               and a lit surface in front, which is what makes a flat shape look like a tree. Its
               top edge is lobed rather than smooth, because cypress foliage grows in tufts and a
               clean outline reads as a cartoon. The limbs are tapered shapes rather than even
               strokes, so they thin out the way a branch does. It stays a silhouette throughout:
               this mark is also 26 pixels wide in a toolbar. -->

          <!-- the outcrop: a ledge, in rock colour rather than in the palette, because anything
               bright under a tree reads as a plant pot. Kept low and asymmetric so it is a piece
               of a cliff and not a plinth. -->
          <path fill="#34566b" d="M18.2 55.6 C18.8 53.4 20.8 51.4 23.6 50.4 C26 49.6 28.4 49.6 30.4 50
            C32 50.3 33.2 50.2 34.8 50 C37.8 49.6 40.6 50.4 42.6 52 C44.2 53.2 45 54.4 45.4 55.6 Z"/>
          <path fill="#4a7085" opacity="0.8" d="M23.6 50.4 C26 49.6 28.4 49.6 30.4 50 C32 50.3 33.2 50.2 34.8 50
            C37.8 49.6 40.6 50.4 42.6 52 C40.2 51.2 37.4 50.9 34.8 51.2 C33 51.4 31.6 51.5 30 51.2
            C27.6 50.8 25.4 50.9 23.6 50.4 Z"/>
          <g stroke="#28414f" stroke-width="0.8" stroke-linecap="round" opacity="0.75">
            <path d="M24.4 52.4 C24.1 53.6 24 54.6 24 55.5"/>
            <path d="M31.4 52.2 C31.2 53.6 31.1 54.6 31.2 55.5"/>
            <path d="M38.6 52.4 C39.2 53.4 39.6 54.6 39.8 55.5"/>
          </g>

          <!-- the trunk: a root flare into the rock, a lean, a taper -->
          <path fill="#2f8a64" d="M26.8 51 C27.4 47.4 28 43.6 29 39.2 C29.5 37 30 35.1 30.6 33.4
            L33.2 34 C32.6 35.7 32.1 37.6 31.7 39.6 C30.9 44 30.6 47.6 30.8 51
            C29.6 50.5 28 50.5 26.8 51 Z"/>

          <!-- the limbs, tapered, forking where the photograph forks -->
          <path fill="#2f8a64" d="M30.4 35 C27.2 33 23.4 31.4 18.8 30.3 L18.4 31.9
            C22.8 33.2 26.4 34.9 29.4 37.2 Z"/>
          <path fill="#2f8a64" d="M31.6 33.2 C33.4 30.2 35.8 27.6 38.8 25.6 L39.8 26.8
            C37.2 29.1 35.2 31.6 33.8 34.6 Z"/>
          <path fill="#2f8a64" d="M31 33.8 C34.8 31.2 39.4 29.2 45 28.2 L45.2 29.8
            C40.2 31.1 36.2 33 32.9 35.6 Z"/>
          <g stroke="#2f8a64" stroke-width="0.85" stroke-linecap="round">
            <path d="M30 38.6 C27.6 37.2 25.4 36.4 23 36"/>
            <path d="M27.2 33.4 C25.2 32.4 23.4 31.8 21.4 31.4"/>
            <path d="M34.2 30.6 C35.6 28.8 37 27.6 38.8 26.4"/>
            <path d="M37.4 30.4 C39.8 29.2 42.4 28.4 45.2 28"/>
          </g>

          <!-- the crown behind: the mass the light does not reach -->
          <path fill="#0d8f5a" d="
            M17.4 28.6 C14.4 27.6 14.4 25.2 17.4 24 C19.8 23 22.8 23 24.8 23.8
            C25 20.8 28.8 18.6 33.2 18.8 C36 16.6 40.4 16.6 43 18.6
            C47.2 18.2 51 19.6 52.2 21.8 C55.4 22.6 56.4 24.6 54.6 26
            C52.8 27.4 49 27.8 45 27.2 C42.8 28.6 39 29.2 35.4 28.6
            C32.4 29.8 28 29.8 25 28.8 C22.6 29.8 19.2 29.4 17.4 28.6 Z"/>
          <!-- the crown in front: the lit surface, lobed along the top -->
          <path fill="#16c47f" d="
            M19 26.2
            C17 25.2 17.4 23.4 19.6 22.7 C21.2 22.2 22.8 22.4 23.8 23
            C24.2 20.9 26.4 19.5 28.8 19.2
            C29.8 17.5 32.4 16.7 34.6 17.4
            C36.1 15.9 38.9 15.9 40.6 17.4
            C43.2 17.1 45.7 18.1 46.6 19.8
            C49.2 20 50.9 21.2 50.5 22.6
            C52.3 23 52.9 24.2 51.7 25
            C50.3 26 47 26.2 44 25.6
            C41.8 26.6 38.4 27 35.4 26.4
            C32.8 27.4 29 27.4 26.4 26.4
            C24.2 27.2 20.8 27 19 26.2 Z"/>
          <!-- the trailing plate, low and downwind -->
          <path fill="#0d8f5a" d="M35 28.6 C38.4 27.3 43.4 27 47.8 27.5 C50.4 27.8 51.8 28.4 51.4 29
            C50.8 29.8 47 30.4 43 30.2 C38.8 30 36.1 29.4 35 28.6 Z"/>
          <!-- the left limb carries its own tuft, the way it does in the photograph -->
          <path fill="#0d8f5a" d="M16.6 30.8 C18.6 29.6 21.6 29.2 23.8 29.8 C25 30.1 25.4 30.7 24.8 31.2
            C23.6 32.1 20.8 32.4 18.6 31.9 C17.2 31.6 16.4 31.2 16.6 30.8 Z"/>
          <path fill="#16c47f" d="M17.4 30 C19.2 29 21.8 28.7 23.6 29.2 C24.6 29.5 24.9 30 24.4 30.4
            C23.4 31.2 21 31.4 19.2 31 C18 30.7 17.3 30.3 17.4 30 Z"/>
          <!-- tufts where the light catches the top -->
          <g stroke="#52e49e" stroke-width="0.85" stroke-linecap="round" opacity="0.7">
            <path d="M26.2 19.6 C27.2 18.9 28.4 18.5 29.6 18.4"/>
            <path d="M34.4 17.4 C35.8 16.7 37.6 16.6 39 17"/>
            <path d="M43.2 19.2 C44.6 19.2 45.8 19.6 46.6 20.4"/>
            <path d="M20.6 23.2 C21.6 22.7 22.6 22.6 23.4 22.8"/>
          </g>
        </g>
          </svg>
        </div>
        <div class="cl-word">gene<span class="cl-dot">.</span>clinic</div>
        <div class="cl-tag">{{ clinicTag }}</div>
      </div>

      <!-- Arriving from "Free version (with limited features)": say what the free tier IS before asking
           anyone to sign in for it. The generic product pitch and the demo links are replaced
           rather than added to -- someone who has already chosen the free version does not
           need to be sold the product again, they need to know what they are getting. -->
      <div class="freeuse" *ngIf="freeMode">
        <div class="fu-title">Free version</div>
        <ul class="fu-list">
          <li><b>Editing is unlimited.</b> Load, edit, save and browse as much as you like.</li>
          <li>Limited GPU off-target analysis</li>
          <li>Limited use of AI/ML models</li>
          <li>Limited use of data resources</li>
        </ul>
      </div>

      <!-- gene.clinic: understanding a gene, not operating a design tool.
           Empty by default now. The heading, the bullets and the note were each saying a
           version of what the banner and the tagline above them already say, and on a
           sign-in page every extra line is one more thing between arriving and starting.
           The block draws only when env.js puts something in it, so this is a wording
           change on the server rather than a rebuild. -->
      <div class="clinic" *ngIf="clinicMode && (clinicTitle || clinicFeatures?.length || clinicNote)">
        <div class="cl-title" *ngIf="clinicTitle">{{ clinicTitle }}</div>
        <ul class="cl-list" *ngIf="clinicFeatures?.length">
          <li *ngFor="let f of clinicFeatures">{{ f }}</li>
        </ul>
        <!-- The sign-in card carries no medical disclaimer: this screen shows nobody's
             genetic information, and the owner asked for it off. The note that remains is
             whatever env.js sets, which is a sentence about where donations go. -->
        <p class="cl-note" *ngIf="clinicNote">{{ clinicNote }}</p>
      </div>

      <ul class="features" *ngIf="!freeMode && !clinicMode">
        <li *ngFor="let f of features">{{ f }}</li>
      </ul>

      <!-- The offer, and nothing beside it. A family who has been told a gene name is not
           looking for a product tour; they are deciding whether to start, and a second
           option next to the one action worth taking only asks them to decide twice. -->
      <div class="demo-row" *ngIf="!freeMode && !clinicMode">
        <a class="demo-link" href="assets/demo/index.html" target="baja-demo"
           (click)="openDemo($event, 'index.html')">Scientists</a>
        <a class="demo-link demo-link--alt" href="assets/demo/for-you.html" target="baja-demo-curious"
           (click)="openDemo($event, 'for-you.html')">Non-scientists</a>
      </div>

      <div class="providers" *ngIf="hasConfigured">
        <button *ngFor="let p of providers"
                class="pbtn" [class.dark]="isDark(p)"
                [disabled]="busy"
                [title]="'Login with ' + p.name"
                (click)="signIn(p)">
          <span class="glyph" *ngIf="showGlyph(p)" [innerHTML]="glyph(p)"></span>
          <span class="ptext">Login with {{ p.name }}</span>
        </button>
      </div>

      <div class="empty" *ngIf="!hasConfigured">
        <p class="hint">No providers configured yet. Add a client ID in <code>window.env</code>
          (e.g. <code>oidc.google.clientId</code>).</p>
      </div>

      <div class="notice" *ngIf="idleSignedOut && !error" role="status">
        You were signed out after an hour of inactivity. Sign in again to continue.
      </div>
      <div class="error" *ngIf="error">{{ error }}</div>

      <div class="signup">
        <span class="secure">🔒 Authorization Code + PKCE</span>
        <div class="su-row">
          {{ clinicMode ? 'First time here?' : 'New here?' }}
          <button class="su-link" type="button" (click)="signUp()">Create an account</button>
        </div>
      </div>

      <a class="enterprise-btn" *ngIf="!clinicMode"
         href="mailto:contact@baja.bio?subject=Baja.bio%20enterprise%20inquiry">
        ✉ Contact us for enterprise use
      </a>

      <!-- A family arriving here has a question about a person, not a procurement question. -->
      <a class="enterprise-btn" *ngIf="clinicMode"
         href="mailto:contact@baja.bio?subject=gene.clinic%20-%20question%20from%20a%20family%20or%20clinician">
        ✉ Ask us a question
      </a>
    </div>
  </div>
  `,
  styles: [`

    /* ---- Phone: the whole page, and it scrolls ----------------------------------
       It used to be trimmed to one screen: the banner's sentence, what this is for, the
       tier lists and the small print were all hidden so that nothing fell below the fold.
       That left a phone showing a logo and two sign-in buttons, with no way to read any of
       it -- the page could not scroll, so the only way on was to sign in.

       Nothing is hidden now. Instead this element is the scroller: the page starts at the
       top and a thumb reaches the rest. Nothing in the app above it can scroll (html, body,
       app-root, the shell and the route host are all overflow:hidden), so the scrolling has
       to happen here, which means the min-height that pushed it past the viewport has to
       go and the height has to come from the host instead.

       The palm is fixed rather than absolute: an absolutely positioned child of a scroller
       scrolls with the content, and the artwork would slide off the top. The gradients
       behind it stay put on their own -- an element's background does not scroll with its
       content. ---- */
    .login-wrap.mobile {
      flex: 1 1 auto; min-height: 0; height: auto; max-height: 100%;
      overflow-y: auto; overflow-x: hidden;
      -webkit-overflow-scrolling: touch; overscroll-behavior: contain;
      align-items: flex-start; justify-content: center;
      padding: 18px 16px 32px;
    }
    .login-wrap.mobile .login-card { padding: 22px 18px; width: 100%; max-width: 440px; margin: 0 auto; }
    .login-wrap.mobile .palm-bg { opacity: .35; position: fixed; }
    /* The two demo links sit side by side on a desktop; on a phone they are two full-width
       taps instead, which is the same shape as the sign-in buttons under them. */
    .login-wrap.mobile .demo-row { flex-direction: column; gap: 10px; }
    .login-wrap.mobile .demo-link { width: 100%; box-sizing: border-box; text-align: center; }
    .login-wrap.mobile .head { margin-bottom: 14px; text-align: center; }
    .login-wrap.mobile .logo img { width: 60px; height: 60px; }
    /* Wide, full-width sign-in buttons: the icon and the "Login with ..." label on one row,
       tall enough for a thumb. (Icon-only squares were too small to tap reliably.) */
    .login-wrap.mobile .providers { display: flex; flex-direction: column; gap: 12px; width: 100%; }
    .login-wrap.mobile .pbtn { width: 100%; min-height: 52px; padding: 0 16px; border-radius: 12px; display: flex; align-items: center; justify-content: center; gap: 12px; font-size: 15px; -webkit-tap-highlight-color: rgba(26,163,189,0.25); touch-action: manipulation; }
    .login-wrap.mobile .pbtn .glyph { display: inline-flex; align-items: center; transform: none; pointer-events: none; }
    .login-wrap.mobile .pbtn .ptext { display: inline; font-size: 15px; font-weight: 600; pointer-events: none; }
    .login-wrap.mobile .signup { margin-top: 18px; text-align: center; }
    .login-wrap.mobile .su-link { font-size: 15px; padding: 10px 6px; }
    :host { display:block; width:100%; height:100%; }
    .login-wrap {
      position:relative; overflow:hidden;
      min-height: 100vh; display:flex; align-items:center; justify-content:center;
      padding: 24px; box-sizing:border-box;
      background:
        radial-gradient(1200px 600px at 15% -10%, rgba(22,196,127,0.25), transparent 60%),
        radial-gradient(1000px 600px at 110% 10%, rgba(18,167,232,0.28), transparent 55%),
        radial-gradient(900px 700px at 50% 120%, rgba(255,140,26,0.20), transparent 55%),
        #071b2a;
      font-family: "Segoe UI", system-ui, -apple-system, Roboto, Arial, sans-serif;
    }
    .trial-row { margin: 14px 0 6px; text-align:center; }
    .trial-btn {
      width:100%; box-sizing:border-box; cursor:pointer;
      border:0; border-radius:10px; padding:13px 18px;
      font: 700 15px "Segoe UI", system-ui, Arial, sans-serif;
      color:#04210f; background: linear-gradient(180deg,#3ddc84,#16c47f);
      box-shadow: 0 6px 18px rgba(22,196,127,0.35);
    }
    .trial-btn:hover:not(:disabled) { filter: brightness(1.06); }
    .trial-btn:disabled { opacity:.6; cursor:default; }
    .trial-sub { margin-top:7px; font: 12px "Segoe UI", system-ui, Arial, sans-serif; color:#9fc7b6; }

    .clinic { margin: 4px 0 14px; text-align:left; }
    .cl-title { font: 700 15px "Segoe UI", system-ui, Arial, sans-serif; color:#eaf6ff; margin-bottom:10px; }
    .cl-list { margin:0; padding-left:18px; }
    .cl-list li { font: 13px/1.7 "Segoe UI", system-ui, Arial, sans-serif; color:#cfe3f0; }
    .cl-note {
      margin: 12px 0 0; padding: 9px 11px; border-radius: 8px;
      background: rgba(255,214,10,0.10); border-left: 3px solid #ffd60a;
      font: 12px/1.55 "Segoe UI", system-ui, Arial, sans-serif; color:#e6dcae;
    }
    .cl-note b { color:#ffd60a; }

    .freeuse { margin: 4px 0 14px; text-align:left; }
    .fu-title { font: 700 15px "Segoe UI", system-ui, Arial, sans-serif; color:#eaf6ff; margin-bottom:10px; }
    .fu-list { margin:0; padding-left:18px; }
    .fu-list li { font: 13px/1.6 "Segoe UI", system-ui, Arial, sans-serif; color:#cfe3f0; }
    .fu-list b { color:#eaf6ff; }

    .palm-bg {
      position:absolute; inset:0; overflow:hidden; pointer-events:none; z-index:0;
      display:flex; align-items:flex-end; justify-content:center;
      filter: blur(14px) saturate(1.15) brightness(0.95);
      opacity:0.55;
    }
    .palm-bg svg { width: min(120vw, 900px); height:100%; transform: translateY(4%); }
    .login-card {
      position:relative; z-index:1; width: 100%; max-width: 400px;
      background: rgba(10,25,40,0.72); backdrop-filter: blur(14px);
      border: 1px solid rgba(18,194,224,0.28); border-radius: 18px;
      padding: 30px 28px 22px; overflow:hidden;
      box-shadow: 0 24px 70px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.03) inset;
    }
    .brandline {
      position:absolute; top:0; left:0; right:0; height:5px;
      background: linear-gradient(90deg, #16c47f, #12c2c2, #12a7e8, #ff8c1a);
    }
    .beta-banner {
      display:flex; align-items:center; justify-content:center; gap:9px; flex-wrap:wrap;
      margin: 4px 0 18px; padding: 9px 12px; border-radius: 11px;
      background: linear-gradient(90deg, rgba(22,196,127,0.18), rgba(18,167,232,0.18), rgba(255,140,26,0.18));
      border: 1px solid rgba(18,194,224,0.35);
      box-shadow: 0 4px 16px rgba(0,0,0,0.25) inset;
    }
    .beta-tag {
      font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.5px;
      color:#0a2540; background:#ffca28; border-radius:6px; padding:3px 8px;
      box-shadow: 0 2px 6px rgba(0,0,0,0.25);
    }
    .beta-text { font-size:13.5px; font-weight:600; color:#eaf6f9; letter-spacing:.2px; }
    .head { text-align:center; margin-bottom: 22px; }
    .clinic-head { margin-bottom: 18px; }
    .cl-mark {
      width:72px; height:72px; margin:6px auto 10px; border-radius:20px;
      display:flex; align-items:center; justify-content:center;
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(22,196,127,0.40);
      box-shadow: 0 6px 18px rgba(0,0,0,0.30);
    }
    .cl-word {
      font: 300 27px/1.1 "Segoe UI", system-ui, -apple-system, Arial, sans-serif;
      color:#eaf6ff; letter-spacing:.6px;
    }
    .cl-dot { color:#16c47f; font-weight:600; }
    .cl-tag {
      margin-top:6px; font: 12px "Segoe UI", system-ui, Arial, sans-serif;
      color:#8fb8c8; letter-spacing:1.4px; text-transform:uppercase;
    }
    .logo {
      width:72px; height:72px; margin:6px auto 12px; border-radius:18px;
      display:flex; align-items:center; justify-content:center; overflow:hidden;
      background:#ffffff; border: 1px solid rgba(18,194,224,0.35);
      box-shadow: 0 6px 18px rgba(0,0,0,0.3);
    }
    .logo img { width:100%; height:100%; object-fit:contain; padding:7px; box-sizing:border-box; }
    h1 { margin:0; font-size:22px; font-weight:700; color:#eaf6f9; letter-spacing:.2px; }
    .sub { margin:6px 0 0; font-size:13px; color:#9db6c4; }
    .feat-head { text-align:center; font-size:10.5px; font-weight:800; letter-spacing:.6px;
      text-transform:uppercase; color:#19d0d0; margin: 0 0 9px; }
    .features { list-style:none; margin:0 0 18px; padding:0 4px; }
    .features li { font-size:12.5px; line-height:1.4; color:#cfe3ea; margin:0 0 7px;
      padding-left:20px; position:relative; }
    .features li::before { content:"✓"; position:absolute; left:2px; top:0; color:#16c47f;
      font-weight:900; font-size:12px; }
    .tour-label { text-align:center; font-size:10.5px; font-weight:800; letter-spacing:.6px;
      text-transform:uppercase; color:#19d0d0; margin: 0 0 8px; }
    /* sunset beach: warm gold -> orange -> coral (match the subscription page) */
    .demo-row { display:flex; flex-wrap:nowrap; gap:10px; margin: 0 0 18px; }
    .demo-link { display:inline-block; font-size:13px; font-weight:800; cursor:pointer;
      color:#3a1500; text-decoration:none; background: linear-gradient(135deg,#ffd166,#ff9a3c,#ff6b81);
      border-radius:10px; padding:10px 14px; box-shadow:0 6px 16px rgba(255,120,80,0.38);
      transition: transform .1s ease, filter .15s ease; flex:1 1 0; text-align:center; }
    .demo-link--alt { color:#4a1224; background: linear-gradient(135deg,#ffb15a,#ff7a8a,#ff5e8a); }
    .demo-link:hover { filter:brightness(1.06); transform: translateY(-1px); }
    .providers { display:flex; flex-direction:column; gap:11px; }
    .pbtn {
      display:flex; align-items:center; gap:12px; width:100%;
      padding: 11px 14px; border-radius: 11px; cursor:pointer;
      background:#ffffff; color:#1b2b36; font-size:14px; font-weight:600;
      border:1px solid rgba(0,0,0,0.08);
      transition: transform .08s ease, box-shadow .15s ease, filter .15s ease;
    }
    .pbtn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0,0,0,0.35); }
    .pbtn:active:not(:disabled) { transform: translateY(0); }
    .pbtn:disabled { opacity:.6; cursor:default; }
    .pbtn.dark { background:#151b23; color:#f4f6f8; border-color: rgba(255,255,255,0.12); }
    .glyph { display:flex; width:22px; justify-content:center; }
    .ptext { flex:1; text-align:left; }
    .badge {
      font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.4px;
      color:#0a2540; background:#ffca28; border-radius:6px; padding:2px 6px;
    }
    .empty { text-align:center; color:#9db6c4; font-size:13px; }
    .empty .hint { font-size:12px; color:#7e97a6; margin-top:10px; line-height:1.6; }
    code { color:#12c2e0; background: rgba(18,194,224,0.12); padding:1px 5px; border-radius:5px; font-size:11px; }
    .error {
      margin-top:16px; padding:10px 12px; border-radius:9px; font-size:13px;
      color:#ffd9c2; background: rgba(255,90,40,0.14); border:1px solid rgba(255,140,26,0.4);
    }
    .notice {
      margin-top:16px; padding:10px 12px; border-radius:9px; font-size:13px;
      color:#bfeef6; background: rgba(18,194,224,0.10); border:1px solid rgba(18,194,224,0.45);
    }
    .signup { margin-top:20px; text-align:center; }
    .secure { font-size:11.5px; color:#7e97a6; }
    .su-row {
      margin-top:14px; padding-top:14px; border-top:1px solid rgba(255,255,255,0.08);
      font-size:13px; color:#9db6c4;
    }
    .su-link {
      background:none; border:none; cursor:pointer; padding:0 2px;
      font-size:13px; font-weight:700; color:#12c2e0; text-decoration:underline;
    }
    .su-link:hover { color:#16c47f; }
    .enterprise-btn { display:block; width:100%; box-sizing:border-box; margin-top:16px; padding:10px 14px;
      text-align:center; text-decoration:none; font-size:13px; font-weight:800; color:#bfeef6;
      background: rgba(18,194,224,0.10); border:1px solid rgba(18,194,224,0.45); border-radius:11px;
      transition: transform .1s ease, background .15s ease, box-shadow .15s ease; }
    .enterprise-btn:hover { transform: translateY(-1px); background: rgba(18,194,224,0.18);
      box-shadow:0 8px 20px rgba(18,167,232,0.25); }
  `],
})
export class LoginComponent {

  // A phone (or an iPad reporting a desktop UA but with touch): the page is trimmed to what
  // fits on a small screen. Same rule as the lionscript isMobile().
  mobile: boolean = (() => {
    try {
      const ua = navigator.userAgent || '';
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|Mobile/i.test(ua)) return true;
      if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true;
      return Math.min(window.innerWidth || 9999, window.innerHeight || 9999) <= 480;
    } catch (e) { return false; }
  })();
  providers: OidcProvider[] = [];
  busy = false;
  error = '';

  // Free-use arrival: app.component's "Free version (with limited features)" sends ?free=1. Read from
  // the URL rather than from a service so a shared or bookmarked link lands on the same page,
  // and so a normal /login is completely unaffected.
  get freeMode(): boolean {
    try { return /(^|[?&])free=1(&|$)/.test('' + window.location.search); }
    catch (e) { return false; }
  }

  /**
   * gene.clinic — the same application, entered by someone who is not a bench scientist.
   *
   * Read from the HOSTNAME, not from a route or a stored preference: the domain someone typed
   * is the most reliable statement of who they think they are, it survives a bookmark and a
   * shared link, and it cannot drift out of step with a setting somewhere else.
   *
   * ?clinic=1 forces it on, because the hostname cannot be gene.clinic on localhost and a page
   * nobody can open on their own machine is a page nobody will look at before it ships.
   */
  get clinicMode(): boolean {
    try {
      // A visitor who explicitly chose the free version wins over the hostname: a share
      // recipient is sent to /login?free=1, and on gene.clinic that must show the
      // free-version wording, not the family/clinic pitch.
      if (this.freeMode) return false;
      if (/(^|[?&])clinic=1(&|$)/.test('' + window.location.search)) return true;
      const h = ('' + window.location.hostname).toLowerCase();
      return h === 'gene.clinic' || h.endsWith('.gene.clinic');
    } catch (e) { return false; }
  }

  /**
   * What gene.clinic says instead of the product pitch.
   *
   * Deliberately about UNDERSTANDING rather than doing. The scientist card sells capabilities
   * -- design siRNA, export IDT codes -- which to a family reads as a list of things they are
   * about to be asked to operate. What a parent, a patient or a referring physician arrives
   * wanting is to make sense of a gene and a variant somebody has just named at them.
   *
   * Overridable the same way the scientist list is, via window.env['clinicFeatures'].
   */
  // No bullets. The list went from five to one to none as the owner read the page back,
  // and the last one said what the note under it said. env.js can still set a list and it
  // will draw; nothing is compiled in for it to fall back to.
  clinicFeatures: string[] = (() => {
    const v = (typeof window !== 'undefined' && (window as any)['env']?.['clinicFeatures']);
    return Array.isArray(v) ? v : [];
  })();

  /**
   * The rest of the gene.clinic wording, in one place and overridable from env.js the same
   * way the lists are. These are on the first screen a family sees, and a first screen is
   * worded and re-worded; having to rebuild and redeploy the whole front end to change a
   * sentence is how a first screen ends up saying something nobody means any more.
   *
   * The medical disclaimer in the template is deliberately NOT here.
   */
  clinicBanner: string = ((typeof window !== 'undefined' && (window as any)['env']?.['clinicBanner'])
    || 'No charge to run a campaign, 100% of the funds you raise go right into your cause.');
  clinicTag: string = ((typeof window !== 'undefined' && (window as any)['env']?.['clinicTag'])
    || 'Understand it. Share it. Fund it.');
  // No heading above the list any more, at the owner's request. The banner, the wordmark
  // and the tagline have all already said what this is by the time the reader gets here,
  // and a fourth line saying it again was the one that could go. Nothing is hard-coded in
  // its place: set clinicTitle in env.js and the heading comes back, leave it and there is
  // no heading and no empty space where one would have been.
  clinicTitle: string = ('' + ((typeof window !== 'undefined' && (window as any)['env']?.['clinicTitle']) || ''));
  // The yellow-barred note is gone with the bullets. It said what the banner says, at more
  // length, in the place a reader has already decided to sign in. Still settable in env.js
  // if it is ever wanted back.
  clinicNote: string = ('' + ((typeof window !== 'undefined' && (window as any)['env']?.['clinicNote']) || ''));


  // Product highlights shown on the login card. Overridable via window.env['loginFeatures'].
  features: string[] = ((typeof window !== 'undefined' && (window as any)['env']?.['loginFeatures']) || [
    'Visualize genes, variants & mutations in context',
    'Design siRNA, ASO & qPCR assays — with djPrimer',
    'Splicing & RNA-binding predictions — BajaSplice, BajaCLIP',
    'Export oligos to IDT order codes',
    'Bonus book: “The Chemistry of RNA Therapeutics”',
  ]);

  constructor(
    private auth: OidcAuthService,
    private sanitizer: DomSanitizer,
    private msal: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
  ) {
    // Only surface providers that are actually supported/configured. Facebook is
    // intentionally excluded as a sign-in option.
    this.providers = this.auth.providers.filter(p => p.configured && p.id !== 'facebook');
  }

  get hasConfigured(): boolean { return this.providers.length > 0; }

  // /login?reason=idle: OidcAuthService.idleLogout sent us here. Say why, once, so an
  // empty editor after lunch reads as a rule and not as a crash.
  get idleSignedOut(): boolean {
    try { return /(^|[?&])reason=idle(&|$)/.test('' + window.location.search); }
    catch (e) { return false; }
  }

  // Open a public, no-login feature tour: scientist carousel (index.html) or the
  // plain-language "for the curious" tour (for-you.html), in its own window.
  openDemo(ev?: Event, page: string = 'index.html') {
    if (ev) ev.preventDefault();
    const safe = page === 'for-you.html' ? 'for-you.html' : 'index.html';
    const win = safe === 'for-you.html' ? 'baja-demo-curious' : 'baja-demo';
    window.open('assets/demo/' + safe, win,
      'width=860,height=820,menubar=no,toolbar=no,location=no,status=no');
  }

  /**
   * "Create an account" → the Oligodesigner (Microsoft Entra External ID / CIAM) sign-up /
   * sign-in page, when that provider is configured. Falls back to the legacy Azure AD B2C
   * sign-up flow otherwise.
   */
  signUp() {
    const ciam = this.auth.getProvider('oidc');
    if (ciam && ciam.configured) {
      this.error = '';
      this.busy = true;
      this.auth.login('oidc').catch((e: any) => { this.busy = false; this.error = e?.message || String(e); });
      return;
    }
    const policies = (typeof window !== 'undefined' && (window as any)['env']?.['auth'] === 'raredb')
      ? rarePolicies : b2cPolicies;
    const req: RedirectRequest = { authority: policies.authorities.signUp.authority, scopes: [] };
    if (this.msalGuardConfig.authRequest) {
      this.msal.loginRedirect({ ...this.msalGuardConfig.authRequest, ...req } as RedirectRequest);
    } else {
      this.msal.loginRedirect(req);
    }
  }

  isDark(p: OidcProvider): boolean {
    const c = (p.brand || '').toLowerCase();
    return c === '#000000' || c === '#24292f' || c === '#151b23';
  }

  glyph(p: OidcProvider): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(p.glyph);
  }

  /**
   * Whether a provider button carries its mark.
   *
   * Google's and Microsoft's glyphs ARE the reassurance -- you recognise the mark before you
   * read the word, and that is most of why those buttons work. The generic OIDC glyph is not
   * that: it is a stand-in for "some single sign-on provider", and on gene.clinic that button
   * now carries the site's own name. A placeholder mark next to your own name reads as a third
   * party the visitor does not recognise, which is the opposite of what it is there to do.
   *
   * So it is dropped for the generic provider on the clinic door only. Every other button, and
   * every button on oligodesigner.com, keeps its mark.
   */
  showGlyph(p: OidcProvider): boolean {
    return !(this.clinicMode && p && p.id === 'oidc');
  }

  async signIn(p: OidcProvider) {
    if (!p.configured) return;
    this.error = '';
    this.busy = true;
    try {
      await this.auth.login(p.id);   // redirects away
    } catch (e: any) {
      this.busy = false;
      this.error = e?.message || String(e);
    }
  }
}
